/* eslint-disable @typescript-eslint/no-non-null-assertion */
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import { Badge, Box, Skeleton, Stack, Tooltip, useTheme } from "@mui/material";
import { QueryStatus } from "@reduxjs/toolkit/query";
import React from "react";

import {
  SingleMixSummary,
  SteelGrade,
  SteelGradeRisk,
  useSummarisePlanResultsMutation,
} from "src/store/api/generatedApi";

import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";

import { useNumberSerialiser } from "hooks/serialisers/numbers";
import { BaseSteelGradesCell } from "./BaseSteelGradesCell";
import { SteelGradeChip } from "./SteelGradeChip";
import { PlanId } from "../boundary/PlanId";
import { EditableDescription } from "./EditableDescription";

interface Props {
  planId: PlanId;
  period: number;
  mixId: number;
  steelGrades: SteelGrade[];
  selectedSteelGradeIds: number[];
  onClick?: (id: number) => void;
  focusedSteelGradeId?: number | null;
  description?: string;
  setDescription?: (description: string) => void;
  preloadedSummary?: SingleMixSummary | null;
}

interface BodyProps {
  selectedSteelGradeIds: number[];
  steelGrades: SteelGrade[];
  steelGradeRisks: SteelGradeRisk[];
  onClick?: (id: number) => void;
  focusedSteelGradeId?: number | null;
  description?: string;
  setDescription?: (description: string) => void;
}

interface BadgedSteelGradeChipProps {
  label: string;
  selected: boolean;
  focused: boolean;
  expectedFailureRate: number;
  weightPercentMaxTolerance: number;
  variant: "warning" | "error";
  onClick?: () => void;
}

interface WarnedSteelGradeRisk {
  variant: "warning";
  id: number;
  expectedOverToleranceRatePercent: number;
  expectedUnderToleranceRatePercent: number;
  weightPercentMinTolerance: number | null;
  weightPercentMaxTolerance: number;
}

interface ErrorSteelGradeRisk {
  variant: "error";
  id: number;
  expectedOverToleranceRatePercent: number;
  expectedUnderToleranceRatePercent: number;
  weightPercentMinTolerance: number | null;
  weightPercentMaxTolerance: number;
}

interface OkSteelGradeRisk {
  variant: "ok";
  id: number;
  expectedOverToleranceRatePercent: number;
  expectedUnderToleranceRatePercent: number;
  weightPercentMinTolerance: number | null;
  weightPercentMaxTolerance: number;
}

export const isErrorSteelGradeRisk = (steelGradeRisk: SteelGradeRisk) => {
  return (
    steelGradeRisk.expected_over_tolerance_rate_percent >=
    steelGradeRisk.error_level_failure_rate_percent
  );
};

export const isWarningSteelGradeRisk = (steelGradeRisk: SteelGradeRisk) => {
  return (
    !isErrorSteelGradeRisk(steelGradeRisk) &&
    steelGradeRisk.expected_over_tolerance_rate_percent >=
      steelGradeRisk.warning_level_failure_rate_percent
  );
};

export const createVariantSteelGradeRisk = (
  steelGradeRisk: SteelGradeRisk
): WarnedSteelGradeRisk | ErrorSteelGradeRisk | OkSteelGradeRisk => {
  const {
    expected_over_tolerance_rate_percent: expectedOverToleranceRatePercent,
    expected_under_tolerance_rate_percent: expectedUnderToleranceRatePercent,
    weight_percent_min_tolerance: weightPercentMinTolerance,
    weight_percent_max_tolerance: weightPercentMaxTolerance,
    steel_grade_id: id,
  } = steelGradeRisk;
  if (isErrorSteelGradeRisk(steelGradeRisk)) {
    return {
      id,
      expectedOverToleranceRatePercent,
      expectedUnderToleranceRatePercent,
      weightPercentMinTolerance,
      weightPercentMaxTolerance,
      variant: "error",
    };
  } else if (isWarningSteelGradeRisk(steelGradeRisk)) {
    return {
      id,
      expectedOverToleranceRatePercent,
      expectedUnderToleranceRatePercent,
      weightPercentMinTolerance,
      weightPercentMaxTolerance,
      variant: "warning",
    };
  } else {
    return {
      id,
      expectedOverToleranceRatePercent,
      expectedUnderToleranceRatePercent,
      weightPercentMinTolerance,
      weightPercentMaxTolerance,
      variant: "ok",
    };
  }
};

const filterVariantSteelGradeRisk =
  <Variant extends "error" | "warning" | "ok">(variant: Variant) =>
  (
    steelGradeRisk:
      WarnedSteelGradeRisk | ErrorSteelGradeRisk | OkSteelGradeRisk
  ): steelGradeRisk is Variant extends "error"
    ? ErrorSteelGradeRisk
    : Variant extends "warning"
      ? WarnedSteelGradeRisk
      : OkSteelGradeRisk => {
    return steelGradeRisk.variant === variant;
  };

const filterErrorVariantSteelGradeRisk = filterVariantSteelGradeRisk("error");
const filterWarningVariantSteelGradeRisk =
  filterVariantSteelGradeRisk("warning");
const filterOkVariantSteelGradeRisk = filterVariantSteelGradeRisk("ok");

const BadgedSteelGradeChip = ({
  label,
  selected,
  focused,
  expectedFailureRate,
  weightPercentMaxTolerance,
  variant,
  onClick,
}: BadgedSteelGradeChipProps) => {
  const theme = useTheme();
  const { t } = useTenantTranslation();
  const { format: numberFormatter } = useNumberSerialiser();
  const unitFormatter = useUnitsFormatter(false);

  const fill = React.useMemo((): (typeof theme)["palette"]["error"]["main"] => {
    switch (variant) {
      case "error":
        return theme.palette.error.main;
      case "warning":
        return theme.palette.warning.main;
    }
  }, [variant, theme]);

  return (
    <Badge
      sx={{
        svg: {
          fill,
        },
      }}
      badgeContent={
        <Tooltip
          placement="right"
          title={t("riskOfCuGT", {
            weightPercentMaxTolerance,
            expectedFailureRate: numberFormatter(expectedFailureRate, {
              decimalPlaces: 2,
            }),
            massFraction: unitFormatter("mass_fraction", false),
          })}
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, -10],
                  },
                },
              ],
            },
          }}
        >
          <WarningRoundedIcon />
        </Tooltip>
      }
    >
      <SteelGradeChip
        label={label}
        selected={selected}
        focused={focused}
        onClick={onClick}
      />
    </Badge>
  );
};

export const WarnedSteelGradesCell = ({
  selectedSteelGradeIds,
  steelGrades,
  planId,
  period,
  mixId,
  onClick,
  focusedSteelGradeId,
  description,
  setDescription,
  preloadedSummary,
}: Props) => {
  const [, summarisePlanResultsResult] = useSummarisePlanResultsMutation({
    fixedCacheKey: `${planId}-${period}`,
  });

  const summary =
    preloadedSummary !== undefined
      ? preloadedSummary
      : summarisePlanResultsResult.status === QueryStatus.fulfilled
        ? summarisePlanResultsResult.data.mixes.find(
            (result) => result.original_mix_id === mixId
          )?.mix.summary
        : undefined;

  if (summary !== undefined) {
    const steelGradeRisks = summary?.chemistry.find(
      (chemistry) => chemistry.constraint_name === "Cu"
    )?.steel_grade_risks;

    if (steelGradeRisks) {
      return (
        <Body
          steelGrades={steelGrades}
          selectedSteelGradeIds={selectedSteelGradeIds}
          steelGradeRisks={steelGradeRisks}
          onClick={onClick}
          focusedSteelGradeId={focusedSteelGradeId}
          description={description}
          setDescription={setDescription}
        />
      );
    } else {
      return (
        <BaseSteelGradesCell
          steelGrades={steelGrades}
          selectedSteelGradeIds={selectedSteelGradeIds}
          onClick={onClick}
          focusedSteelGradeId={focusedSteelGradeId}
          description={description}
          setDescription={setDescription}
        />
      );
    }
  } else if (
    preloadedSummary === undefined &&
    summarisePlanResultsResult.status !== QueryStatus.fulfilled
  ) {
    return (
      <Box
        sx={{
          display: "flex",
          gap: 0.4,
          flexWrap: "wrap",
          paddingY: 1,
          paddingX: 0.5,
        }}
      >
        <Skeleton
          variant="rounded"
          sx={{ width: 60, height: 20, borderRadius: 16 }}
        />
        <Skeleton
          variant="rounded"
          sx={{ width: 50, height: 20, borderRadius: 16 }}
        />
        <Skeleton
          variant="rounded"
          sx={{ width: 55, height: 20, borderRadius: 16 }}
        />
        <Skeleton
          variant="rounded"
          sx={{ width: 75, height: 20, borderRadius: 16 }}
        />
        <Skeleton
          variant="rounded"
          sx={{ width: 80, height: 20, borderRadius: 16 }}
        />
        <Skeleton
          variant="rounded"
          sx={{ width: 60, height: 20, borderRadius: 16 }}
        />
      </Box>
    );
  } else {
    return (
      <BaseSteelGradesCell
        steelGrades={steelGrades}
        selectedSteelGradeIds={selectedSteelGradeIds}
        onClick={onClick}
        focusedSteelGradeId={focusedSteelGradeId}
        description={description}
        setDescription={setDescription}
      />
    );
  }
};

export const Body = ({
  steelGrades,
  steelGradeRisks,
  selectedSteelGradeIds,
  onClick,
  focusedSteelGradeId,
  description,
  setDescription,
}: BodyProps) => {
  const theme = useTheme();

  const steelGradesNameMap = React.useMemo(() => {
    return new Map(
      steelGrades.map((steelGrade) => [steelGrade.id, steelGrade.name])
    );
  }, [steelGrades]);

  const [
    errorMixSelectedSteelGrades,
    warnedMixSelectedSteelGrades,
    mixSelectedSteelGrades,
    errorNotMixSelectedSteelGrades,
    warnedNotMixSelectedSteelGrades,
    notMixSelectedSteelGrades,
  ] = React.useMemo(() => {
    const selectedSteelGradeRisks = steelGradeRisks
      .filter((steelGradeRisk) =>
        selectedSteelGradeIds.includes(steelGradeRisk.steel_grade_id)
      )
      .map(createVariantSteelGradeRisk);

    const unselectedSteelGradeRisks = steelGradeRisks
      .filter(
        (steelGradeRisk) =>
          !selectedSteelGradeIds.includes(steelGradeRisk.steel_grade_id)
      )
      .map(createVariantSteelGradeRisk);

    return [
      selectedSteelGradeRisks.filter(filterErrorVariantSteelGradeRisk),
      selectedSteelGradeRisks.filter(filterWarningVariantSteelGradeRisk),
      selectedSteelGradeRisks.filter(filterOkVariantSteelGradeRisk),
      unselectedSteelGradeRisks.filter(filterErrorVariantSteelGradeRisk),
      unselectedSteelGradeRisks.filter(filterWarningVariantSteelGradeRisk),
      unselectedSteelGradeRisks.filter(filterOkVariantSteelGradeRisk),
    ] as [
      ErrorSteelGradeRisk[],
      WarnedSteelGradeRisk[],
      OkSteelGradeRisk[],
      ErrorSteelGradeRisk[],
      WarnedSteelGradeRisk[],
      OkSteelGradeRisk[],
    ];
  }, [selectedSteelGradeIds, steelGradeRisks]);

  return (
    <Stack
      sx={{
        height: "100%",
        justifyContent: "space-between",
      }}
    >
      <Stack
        sx={{
          flexDirection: "row",
          gap: 0.4,
          flexWrap: "wrap",
          paddingY: 1,
          paddingX: 0.5,
          overflow: "auto",
          height: `calc(100% - ${theme.spacing(2 + (description ? 5 : 0))})`,
          alignItems: "flex-start",
        }}
      >
        {errorMixSelectedSteelGrades.map((steelGrade) => {
          return (
            <BadgedSteelGradeChip
              key={steelGrade.id}
              label={steelGradesNameMap.get(steelGrade.id)!}
              focused={focusedSteelGradeId === steelGrade.id}
              onClick={() => onClick?.(steelGrade.id)}
              selected
              weightPercentMaxTolerance={steelGrade.weightPercentMaxTolerance}
              expectedFailureRate={steelGrade.expectedOverToleranceRatePercent}
              variant="error"
            />
          );
        })}
        {warnedMixSelectedSteelGrades.map((steelGrade) => {
          return (
            <BadgedSteelGradeChip
              key={steelGrade.id}
              label={steelGradesNameMap.get(steelGrade.id)!}
              focused={focusedSteelGradeId === steelGrade.id}
              onClick={() => onClick?.(steelGrade.id)}
              selected
              weightPercentMaxTolerance={steelGrade.weightPercentMaxTolerance}
              expectedFailureRate={steelGrade.expectedOverToleranceRatePercent}
              variant="warning"
            />
          );
        })}
        {mixSelectedSteelGrades.map((steelGrade) => {
          return (
            <SteelGradeChip
              focused={focusedSteelGradeId === steelGrade.id}
              onClick={() => onClick?.(steelGrade.id)}
              key={steelGrade.id}
              label={steelGradesNameMap.get(steelGrade.id)!}
              selected
            />
          );
        })}
        {[
          ...mixSelectedSteelGrades,
          ...warnedMixSelectedSteelGrades,
          ...errorMixSelectedSteelGrades,
        ].length > 0 ? (
          <Box sx={{ flexBasis: "100%", height: theme.spacing(0.5) }} />
        ) : null}
        {errorNotMixSelectedSteelGrades.map((steelGrade) => {
          return (
            <BadgedSteelGradeChip
              key={steelGrade.id}
              label={steelGradesNameMap.get(steelGrade.id)!}
              focused={focusedSteelGradeId === steelGrade.id}
              onClick={() => onClick?.(steelGrade.id)}
              selected={false}
              weightPercentMaxTolerance={steelGrade.weightPercentMaxTolerance}
              expectedFailureRate={steelGrade.expectedOverToleranceRatePercent}
              variant="error"
            />
          );
        })}
        {warnedNotMixSelectedSteelGrades.map((steelGrade) => {
          return (
            <BadgedSteelGradeChip
              key={steelGrade.id}
              label={steelGradesNameMap.get(steelGrade.id)!}
              focused={focusedSteelGradeId === steelGrade.id}
              onClick={() => onClick?.(steelGrade.id)}
              selected={false}
              weightPercentMaxTolerance={steelGrade.weightPercentMaxTolerance}
              expectedFailureRate={steelGrade.expectedOverToleranceRatePercent}
              variant="warning"
            />
          );
        })}
        {notMixSelectedSteelGrades.map((steelGrade) => {
          return (
            <SteelGradeChip
              key={steelGrade.id}
              label={steelGradesNameMap.get(steelGrade.id)!}
              selected={false}
              focused={focusedSteelGradeId === steelGrade.id}
              onClick={() => onClick?.(steelGrade.id)}
            />
          );
        })}
      </Stack>
      {description !== undefined || setDescription != undefined ? (
        <EditableDescription
          description={description}
          setDescription={setDescription}
        />
      ) : null}
    </Stack>
  );
};
